var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import ADD_AND_ASSIGN_COMPONENTS from 'api/mutations/ADD_AND_ASSIGN_COMPONENTS';
import ADD_COMPONENT from 'api/mutations/ADD_COMPONENT';
import ASSIGN_PRODUCT_TO_COMPONENT from 'api/mutations/ASSIGN_PRODUCT_TO_COMPONENT';
import REMOVE_COMPONENT from 'api/mutations/REMOVE_COMPONENT';
import SET_COMPONENT_HAS_ONE_VARIANT_COLOR_FOR_ALL_SPECIFICATION_COLORS from 'api/mutations/SET_COMPONENT_HAS_ONE_VARIANT_COLOR_FOR_ALL_SPECIFICATION_COLORS';
import SET_COMPONENT_HAS_ONE_VARIANT_SIZE_FOR_ALL_SPECIFICATION_SIZES from 'api/mutations/SET_COMPONENT_HAS_ONE_VARIANT_SIZE_FOR_ALL_SPECIFICATION_SIZES';
import SET_SPECIFICATION_COLOR_TO_VARIANT_COLOR_ASSIGNMENT from 'api/mutations/SET_SPECIFICATION_COLOR_TO_VARIANT_COLOR_ASSIGNMENT';
import SET_SPECIFICATION_SIZE_TO_VARIANT_SIZE_ASSIGNMENT from 'api/mutations/SET_SPECIFICATION_SIZE_TO_VARIANT_SIZE_ASSIGNMENT';
import SET_VARIANT_COLOR_FOR_ALL_SPECIFICATION_COLORS from 'api/mutations/SET_VARIANT_COLOR_FOR_ALL_SPECIFICATION_COLORS';
import SET_VARIANT_SIZE_FOR_ALL_SPECIFICATION_SIZES from 'api/mutations/SET_VARIANT_SIZE_FOR_ALL_SPECIFICATION_SIZES';
import TOGGLE_VARIED_CONSUMPTION from 'api/mutations/TOGGLE_VARIED_CONSUMPTION';
import UPDATE_COMPONENT_CONSUMPTIONS from 'api/mutations/UPDATE_COMPONENT_CONSUMPTIONS';
import GET_COMPONENTS_HEADERS from 'api/queries/GET_COMPONENTS_HEADERS';
import GET_SPECIFICATION_COMPONENT_CARDS from 'api/queries/GET_SPECIFICATION_COMPONENT_CARDS';
import GET_SPECIFICATION_COMPONENT_CONSUMPTIONS from 'api/queries/GET_SPECIFICATION_COMPONENT_CONSUMPTIONS';
import GET_SPECIFICATION_COMPONENT_VARIANTS from 'api/queries/GET_SPECIFICATION_COMPONENT_VARIANTS';
import client from '../api/environment';
const COMPONENT_QUERY_KEYS = ['cards', 'variants', 'consumptions', 'headers'];
const createInitialQueriesState = () => ({
    cards: {
        items: [],
        status: 'idle',
        error: null,
        refetch: false,
        specificationId: null,
        activeRequestId: null,
        invalidationVersion: 0,
        requestInvalidationVersion: 0,
    },
    variants: {
        items: [],
        status: 'idle',
        error: null,
        refetch: false,
        specificationId: null,
        activeRequestId: null,
        invalidationVersion: 0,
        requestInvalidationVersion: 0,
    },
    consumptions: {
        items: [],
        status: 'idle',
        error: null,
        refetch: false,
        specificationId: null,
        activeRequestId: null,
        invalidationVersion: 0,
        requestInvalidationVersion: 0,
    },
    headers: {
        items: [],
        status: 'idle',
        error: null,
        refetch: false,
        specificationId: null,
        activeRequestId: null,
        invalidationVersion: 0,
        requestInvalidationVersion: 0,
    },
});
const initialState = {
    queries: createInitialQueriesState(),
    modalView: 'categories',
    category: null,
    componentName: null,
    componentsToAssign: [],
    status: {
        add: 'idle',
        remove: 'idle',
        other: 'idle',
    },
    errors: {
        add: null,
        remove: null,
        other: null,
    },
};
const getComponentNodes = (response) => response.data.node.components.edges.map((item) => item.node);
const normalizeConnection = (connection) => (Object.assign(Object.assign({}, (connection || {})), { edges: Array.isArray(connection === null || connection === void 0 ? void 0 : connection.edges) ? connection.edges : [] }));
const normalizeVariantComponent = (component) => (Object.assign(Object.assign(Object.assign({}, component), { colors: normalizeConnection(component === null || component === void 0 ? void 0 : component.colors), sizes: normalizeConnection(component === null || component === void 0 ? void 0 : component.sizes) }), ((component === null || component === void 0 ? void 0 : component.productSnapshot)
    ? {
        productSnapshot: Object.assign(Object.assign({}, component.productSnapshot), { variantSnapshots: normalizeConnection(component.productSnapshot.variantSnapshots) }),
    }
    : {})));
export const shouldFetchSpecificationComponentQuery = (status, refetch, cachedSpecificationId, requestedSpecificationId) => cachedSpecificationId !== requestedSpecificationId ||
    status === 'idle' ||
    (refetch && status !== 'loading');
export const fetchSpecificationComponentCards = createAsyncThunk('specificationComponents/fetchSpecificationComponentCards', ({ specificationId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_SPECIFICATION_COMPONENT_CARDS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'no-cache',
        });
        return getComponentNodes(response);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationComponentVariants = createAsyncThunk('specificationComponents/fetchSpecificationComponentVariants', ({ specificationId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_SPECIFICATION_COMPONENT_VARIANTS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'no-cache',
        });
        return getComponentNodes(response).map(normalizeVariantComponent);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationComponentConsumptions = createAsyncThunk('specificationComponents/fetchSpecificationComponentConsumptions', ({ specificationId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_SPECIFICATION_COMPONENT_CONSUMPTIONS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'no-cache',
        });
        return getComponentNodes(response);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationComponentHeaders = createAsyncThunk('specificationComponents/fetchSpecificationComponentHeaders', ({ specificationId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const response = yield client.query({
            query: GET_COMPONENTS_HEADERS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'no-cache',
        });
        return getComponentNodes(response);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addAndAssignSpecificationComponent = createAsyncThunk('specificationComponents/addAndAssignSpecificationComponent', ({ productCategoryId, modelSpecificationId, productId, language, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const addedComponentData = yield client.mutate({
            mutation: ADD_COMPONENT,
            variables: { productCategoryId, modelSpecificationId, language },
        });
        const assignedComponentData = yield client.mutate({
            mutation: ASSIGN_PRODUCT_TO_COMPONENT,
            variables: {
                componentId: addedComponentData.data.addComponent.component.id,
                productId,
            },
        });
        return {
            categoryId: addedComponentData.data.addComponent.component.productCategory.id,
            componentId: assignedComponentData.data.assignProductToComponent.component.id,
            productId: assignedComponentData.data.assignProductToComponent.component.productSnapshot.product.id,
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addAndAssignSpecificationComponents = createAsyncThunk('specificationComponents/addAndAssignSpecificationComponents', ({ components, modelSpecificationId, language, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: ADD_AND_ASSIGN_COMPONENTS,
            variables: {
                modelSpecificationId,
                language,
                components: components.map(({ productCategoryId, productId }) => ({
                    productCategoryId,
                    productId,
                })),
            },
        });
        return {
            components: responseData.data.addAndAssignComponents.components,
            productIds: components.map((component) => component.productId),
        };
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeSpecificationComponent = createAsyncThunk('specificationComponents/removeSpecificationComponent', ({ componentId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const specifiactionComponentData = yield client.mutate({
            mutation: REMOVE_COMPONENT,
            variables: { componentId },
        });
        return Object.assign({ componentId }, specifiactionComponentData.data);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const toggleVariedConsumptionSpecificationComponent = createAsyncThunk('specificationComponents/toggleVariedConsumptionSpecificationComponent', ({ componentId, consumptionVariedOnSize, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: TOGGLE_VARIED_CONSUMPTION,
            variables: { componentId, consumptionVariedOnSize },
        });
        return responseData.data.setComponentConsumptionVariedOnSize.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateConsumptionSpecificationComponent = createAsyncThunk('specificationComponents/updateConsumptionSpecificationComponent', ({ cellId, value }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: UPDATE_COMPONENT_CONSUMPTIONS,
            variables: { componentsConsumption: [[cellId, value]] },
        });
        return responseData.data.updateComponentConsumptions.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const toggleVariedOnColorSpecificationComponent = createAsyncThunk('specificationComponents/toggleVariedOnColorSpecificationComponent', ({ componentId, hasOneVariantColorForAllSpecificationColors, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: SET_COMPONENT_HAS_ONE_VARIANT_COLOR_FOR_ALL_SPECIFICATION_COLORS,
            variables: { componentId, hasOneVariantColorForAllSpecificationColors },
        });
        return responseData.data.setComponentHasOneVariantColorForAllSpecificationColors.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setVariantForAllColor = createAsyncThunk('specificationComponents/setVariantForAllColor', ({ componentId, productVariantColor }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: SET_VARIANT_COLOR_FOR_ALL_SPECIFICATION_COLORS,
            variables: { componentId, productVariantColor },
        });
        return responseData.data.setVariantColorForAllSpecificationColors.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateColorToVariantAssignment = createAsyncThunk('specificationComponents/updateColorToVariantAssignment', ({ componentId, modelSpecificationColorId, productVariantColor, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: SET_SPECIFICATION_COLOR_TO_VARIANT_COLOR_ASSIGNMENT,
            variables: { componentId, modelSpecificationColorId, productVariantColor },
        });
        return responseData.data.setSpecificationColorToVariantColorAssignment.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const toggleVariedOnSizeSpecificationComponent = createAsyncThunk('specificationComponents/toggleVariedOnSizeSpecificationComponent', ({ componentId, hasOneVariantSizeForAllSpecificationSizes, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: SET_COMPONENT_HAS_ONE_VARIANT_SIZE_FOR_ALL_SPECIFICATION_SIZES,
            variables: { componentId, hasOneVariantSizeForAllSpecificationSizes },
        });
        return responseData.data.setComponentHasOneVariantSizeForAllSpecificationSizes.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setVariantForAllSizes = createAsyncThunk('specificationComponents/setVariantForAllSizes', ({ componentId, productVariantSize }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: SET_VARIANT_SIZE_FOR_ALL_SPECIFICATION_SIZES,
            variables: { componentId, productVariantSize },
        });
        return responseData.data.setVariantSizeForAllSpecificationSizes.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateSizeToVariantAssignment = createAsyncThunk('specificationComponents/updateSizeToVariantAssignment', ({ componentId, modelSpecificationSizeId, productVariantSize, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const responseData = yield client.mutate({
            mutation: SET_SPECIFICATION_SIZE_TO_VARIANT_SIZE_ASSIGNMENT,
            variables: { componentId, modelSpecificationSizeId, productVariantSize },
        });
        return responseData.data.setSpecificationSizeToVariantSizeAssignment.component;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const specificationComponentsSlice = createSlice({
    name: 'specificationComponents',
    initialState,
    reducers: {
        resetSpecificationComponentsState(state) {
            state.queries = createInitialQueriesState();
            state.modalView = 'categories';
            state.category = null;
            state.componentName = null;
            state.componentsToAssign = [];
        },
        setComponentRefetchFlag(state, action) {
            COMPONENT_QUERY_KEYS.forEach((queryKey) => {
                const queryState = state.queries[queryKey];
                queryState.refetch = action.payload;
                if (action.payload) {
                    queryState.invalidationVersion += 1;
                }
            });
        },
        setModalView: (state, action) => {
            state.modalView = action.payload;
            if (action.payload !== 'componentPreview') {
                state.componentName = null;
            }
        },
        setComponentName: (state, action) => {
            state.componentName = action.payload;
        },
        setCategory: (state, action) => {
            state.category = action.payload;
        },
        addComponentToAssign: (state, action) => {
            state.componentsToAssign = [...state.componentsToAssign, action.payload];
        },
        removeComponentToAssign: (state, action) => {
            const index = state.componentsToAssign.findIndex((component) => component.productId === action.payload.productId);
            if (index !== -1) {
                state.componentsToAssign.splice(index, 1);
            }
        },
        clearComponentsToAssign: (state) => {
            state.componentsToAssign = [];
        },
        updateStoreConsumption: (state, action) => {
            const { componentId, cellId, value } = action.payload;
            const { items } = state.queries.consumptions;
            const itemIndex = items.findIndex((item) => item.id === componentId);
            if (itemIndex !== -1) {
                const cellIndex = items[itemIndex].consumptions.edges.findIndex((item) => item.node.id === cellId);
                if (cellIndex !== -1) {
                    items[itemIndex].consumptions.edges[cellIndex].node = Object.assign(Object.assign({}, items[itemIndex].consumptions.edges[cellIndex].node), { consumption: value });
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpecificationComponentCards.pending, (state, action) => {
            const queryState = state.queries.cards;
            queryState.status = 'loading';
            queryState.error = null;
            queryState.specificationId = action.meta.arg.specificationId;
            queryState.activeRequestId = action.meta.requestId;
            queryState.requestInvalidationVersion = queryState.invalidationVersion;
        })
            .addCase(fetchSpecificationComponentCards.fulfilled, (state, action) => {
            const queryState = state.queries.cards;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'succeeded';
            queryState.items = action.payload;
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentCards.rejected, (state, action) => {
            const queryState = state.queries.cards;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'failed';
            queryState.error = action.payload || 'Failed to fetch specification components';
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentVariants.pending, (state, action) => {
            const queryState = state.queries.variants;
            queryState.status = 'loading';
            queryState.error = null;
            queryState.specificationId = action.meta.arg.specificationId;
            queryState.activeRequestId = action.meta.requestId;
            queryState.requestInvalidationVersion = queryState.invalidationVersion;
        })
            .addCase(fetchSpecificationComponentVariants.fulfilled, (state, action) => {
            const queryState = state.queries.variants;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'succeeded';
            queryState.items = action.payload;
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentVariants.rejected, (state, action) => {
            const queryState = state.queries.variants;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'failed';
            queryState.error =
                action.payload || 'Failed to fetch specification component variants';
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentConsumptions.pending, (state, action) => {
            const queryState = state.queries.consumptions;
            queryState.status = 'loading';
            queryState.error = null;
            queryState.specificationId = action.meta.arg.specificationId;
            queryState.activeRequestId = action.meta.requestId;
            queryState.requestInvalidationVersion = queryState.invalidationVersion;
        })
            .addCase(fetchSpecificationComponentConsumptions.fulfilled, (state, action) => {
            const queryState = state.queries.consumptions;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'succeeded';
            queryState.items = action.payload;
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentConsumptions.rejected, (state, action) => {
            const queryState = state.queries.consumptions;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'failed';
            queryState.error =
                action.payload || 'Failed to fetch specification component consumptions';
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentHeaders.pending, (state, action) => {
            const queryState = state.queries.headers;
            queryState.status = 'loading';
            queryState.error = null;
            queryState.specificationId = action.meta.arg.specificationId;
            queryState.activeRequestId = action.meta.requestId;
            queryState.requestInvalidationVersion = queryState.invalidationVersion;
        })
            .addCase(fetchSpecificationComponentHeaders.fulfilled, (state, action) => {
            const queryState = state.queries.headers;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'succeeded';
            queryState.items = action.payload;
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(fetchSpecificationComponentHeaders.rejected, (state, action) => {
            const queryState = state.queries.headers;
            if (queryState.activeRequestId !== action.meta.requestId ||
                queryState.specificationId !== action.meta.arg.specificationId) {
                return;
            }
            if (queryState.invalidationVersion > queryState.requestInvalidationVersion) {
                queryState.status = 'idle';
                queryState.activeRequestId = null;
                return;
            }
            queryState.status = 'failed';
            queryState.error =
                action.payload || 'Failed to fetch specification component headers';
            queryState.refetch = false;
            queryState.activeRequestId = null;
        })
            .addCase(addAndAssignSpecificationComponent.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addAndAssignSpecificationComponent.fulfilled, (state, action) => {
            state.status.add = 'succeeded';
            const index = state.componentsToAssign.findIndex((component) => component.productId === action.payload.productId);
            if (index !== -1) {
                state.componentsToAssign.splice(index, 1);
            }
        })
            .addCase(addAndAssignSpecificationComponent.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.payload || 'Failed to add specification component';
        })
            .addCase(addAndAssignSpecificationComponents.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addAndAssignSpecificationComponents.fulfilled, (state, action) => {
            state.status.add = 'succeeded';
            state.componentsToAssign = state.componentsToAssign.filter((component) => !action.payload.productIds.includes(component.productId));
        })
            .addCase(addAndAssignSpecificationComponents.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.payload || 'Failed to add specification components';
        })
            .addCase(removeSpecificationComponent.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeSpecificationComponent.fulfilled, (state, action) => {
            state.status.remove = 'succeeded';
            COMPONENT_QUERY_KEYS.forEach((queryKey) => {
                const queryState = state.queries[queryKey];
                queryState.items = queryState.items.filter((component) => component.id !== action.payload.componentId);
                if (queryState.activeRequestId) {
                    queryState.invalidationVersion += 1;
                    queryState.refetch = true;
                }
            });
        })
            .addCase(removeSpecificationComponent.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove =
                action.payload || 'Failed to remove specification component';
        })
            .addCase(toggleVariedConsumptionSpecificationComponent.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(toggleVariedConsumptionSpecificationComponent.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            const { id, consumptionVariedOnSize, consumptions } = action.payload;
            const { items } = state.queries.consumptions;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign({}, items[itemIndex]), { consumptionVariedOnSize,
                    consumptions });
            }
            state.status.other = 'succeeded';
        })
            .addCase(toggleVariedConsumptionSpecificationComponent.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other =
                action.payload || 'Failed to unassign specification component';
        })
            .addCase(updateConsumptionSpecificationComponent.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(updateConsumptionSpecificationComponent.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            const { id, consumptions } = action.payload;
            const { items } = state.queries.consumptions;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign({}, items[itemIndex]), { consumptions });
            }
            state.status.other = 'succeeded';
        })
            .addCase(updateConsumptionSpecificationComponent.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other =
                action.payload || 'Failed to unassign specification component';
        })
            .addCase(toggleVariedOnColorSpecificationComponent.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(toggleVariedOnColorSpecificationComponent.fulfilled, (state, action) => {
            const { id, hasOneVariantColorForAllSpecificationColors, variantColorForAllSpecificationColors, colors, } = action.payload;
            const { items } = state.queries.variants;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign(Object.assign({}, items[itemIndex]), { hasOneVariantColorForAllSpecificationColors,
                    variantColorForAllSpecificationColors }), (colors ? { colors: normalizeConnection(colors) } : {}));
            }
            state.status.other = 'succeeded';
        })
            .addCase(toggleVariedOnColorSpecificationComponent.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other =
                action.payload || 'Failed to unassign specification component';
        })
            .addCase(setVariantForAllColor.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(setVariantForAllColor.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            const { id, variantColorForAllSpecificationColors, colors } = action.payload;
            const { items } = state.queries.variants;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign(Object.assign({}, items[itemIndex]), { variantColorForAllSpecificationColors }), (colors ? { colors: normalizeConnection(colors) } : {}));
            }
            state.status.other = 'succeeded';
        })
            .addCase(setVariantForAllColor.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other = action.payload || 'Failed to set variant for all color';
        })
            .addCase(updateColorToVariantAssignment.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(updateColorToVariantAssignment.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            const { id, colors } = action.payload;
            const { items } = state.queries.variants;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign({}, items[itemIndex]), (colors ? { colors: normalizeConnection(colors) } : {}));
            }
            state.status.other = 'succeeded';
        })
            .addCase(updateColorToVariantAssignment.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other =
                action.payload || 'Failed to update color to variant assignment';
        })
            .addCase(toggleVariedOnSizeSpecificationComponent.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(toggleVariedOnSizeSpecificationComponent.fulfilled, (state, action) => {
            const { id, hasOneVariantSizeForAllSpecificationSizes, variantSizeForAllSpecificationSizes, sizes, } = action.payload;
            const { items } = state.queries.variants;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign(Object.assign({}, items[itemIndex]), { hasOneVariantSizeForAllSpecificationSizes,
                    variantSizeForAllSpecificationSizes }), (sizes ? { sizes: normalizeConnection(sizes) } : {}));
            }
            state.status.other = 'succeeded';
        })
            .addCase(toggleVariedOnSizeSpecificationComponent.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other = action.payload || 'Failed to toggle varied on size';
        })
            .addCase(setVariantForAllSizes.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(setVariantForAllSizes.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            const { id, variantSizeForAllSpecificationSizes } = action.payload;
            const { items } = state.queries.variants;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign({}, items[itemIndex]), { variantSizeForAllSpecificationSizes });
            }
            state.status.other = 'succeeded';
        })
            .addCase(setVariantForAllSizes.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other = action.payload || 'Failed to set variant for all color';
        })
            .addCase(updateSizeToVariantAssignment.pending, (state) => {
            state.status.other = 'loading';
        })
            .addCase(updateSizeToVariantAssignment.fulfilled, (state, action) => {
            if (!action.payload)
                return;
            const { id, sizes } = action.payload;
            const { items } = state.queries.variants;
            const itemIndex = items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                items[itemIndex] = Object.assign(Object.assign({}, items[itemIndex]), (sizes ? { sizes: normalizeConnection(sizes) } : {}));
            }
            state.status.other = 'succeeded';
        })
            .addCase(updateSizeToVariantAssignment.rejected, (state, action) => {
            state.status.other = 'failed';
            state.errors.other =
                action.payload || 'Failed to update size to variant assignment';
        });
    },
});
export const { resetSpecificationComponentsState, setComponentRefetchFlag, setModalView, setCategory, setComponentName, addComponentToAssign, removeComponentToAssign, clearComponentsToAssign, updateStoreConsumption, } = specificationComponentsSlice.actions;
export const selectSpecificationComponentCards = (state) => state.specificationComponents.queries.cards.items;
export const selectSpecificationComponentCardsStatus = (state) => state.specificationComponents.queries.cards.status;
export const selectSpecificationComponentCardsRefetch = (state) => state.specificationComponents.queries.cards.refetch;
export const selectSpecificationComponentCardsSpecificationId = (state) => state.specificationComponents.queries.cards.specificationId;
export const selectSpecificationComponentVariants = (state) => state.specificationComponents.queries.variants.items;
export const selectSpecificationComponentVariantsStatus = (state) => state.specificationComponents.queries.variants.status;
export const selectSpecificationComponentVariantsRefetch = (state) => state.specificationComponents.queries.variants.refetch;
export const selectSpecificationComponentVariantsSpecificationId = (state) => state.specificationComponents.queries.variants.specificationId;
export const selectSpecificationComponentConsumptions = (state) => state.specificationComponents.queries.consumptions.items;
export const selectSpecificationComponentConsumptionsStatus = (state) => state.specificationComponents.queries.consumptions.status;
export const selectSpecificationComponentConsumptionsRefetch = (state) => state.specificationComponents.queries.consumptions.refetch;
export const selectSpecificationComponentConsumptionsSpecificationId = (state) => state.specificationComponents.queries.consumptions.specificationId;
export const selectSpecificationComponentHeaders = (state) => state.specificationComponents.queries.headers.items;
export const selectSpecificationComponentHeadersStatus = (state) => state.specificationComponents.queries.headers.status;
export const selectSpecificationComponentHeadersRefetch = (state) => state.specificationComponents.queries.headers.refetch;
export const selectSpecificationComponentHeadersSpecificationId = (state) => state.specificationComponents.queries.headers.specificationId;
export const selectSpecificationComponentsModalView = (state) => state.specificationComponents.modalView;
export const selectSpecificationComponentsCategory = (state) => state.specificationComponents.category;
export const selectSpecificationComponentsComponentName = (state) => state.specificationComponents.componentName;
export const selectSpecificationComponentCategoryToAdd = (state) => state.specificationComponents.componentCategoryToAdd;
export const selectSpecificationComponentsToAssign = (state) => state.specificationComponents.componentsToAssign;
export const selectIsComponentAssigned = (productId) => createSelector((state) => state.specificationComponents.componentsToAssign, (componentsToAssign) => componentsToAssign.some((component) => component.productId === productId));
export const selectComponentAssigned = (productId) => createSelector((state) => state.specificationComponents.componentsToAssign, (componentsToAssign) => componentsToAssign.find((component) => component.productId === productId));
export const selectCategoryComponentsAssigned = (categoryId) => createSelector((state) => state.specificationComponents.componentsToAssign, (componentsToAssign) => componentsToAssign.filter((component) => component.productCategoryId === categoryId).length);
export default specificationComponentsSlice.reducer;
